import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HhsgSharedSideNavMenuItem } from '../../models/side-nav-menu-item.model';
import { HhsgSharedSideNavHeader } from '../../models/side-nav-header.model';
import { GuardsCheckEnd, NavigationEnd, Router } from '@angular/router';
import { RouteUtil } from '../../utilities/route.util';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { HhsgSharedConfigService, HhsgSharedUserService } from '../../services';
import { Constants } from '../../utilities/constants.util';
import { HhsgSharedUserProfile } from '../../models';
import { RoleUtil } from '../../utilities/role.util';

@Component({
    selector: 'hhsg-sidenav-bar',
    templateUrl: './sidenav-bar.component.html',
    styleUrls: ['./sidenav-bar.component.scss']
})
export class HhsgSharedSidenavBarComponent implements OnInit, OnDestroy {
    public _headerLinks: HhsgSharedSideNavHeader[] = [];
    isStartRoundSectionVisible: boolean = true;
    private visibilitySubscription: Subscription;
    private applicationAbbreviation;
    user: HhsgSharedUserProfile;
    private _unsubscribe$ = new Subject<void>();

    @Input() set headerLinks(value: HhsgSharedSideNavHeader[]) {
        let refreshSelections = this._headerLinks && value.length && this._headerLinks.length != value.length;

        this._headerLinks = value;

        if (refreshSelections) {
            this.updateSelectedItems();
        }
    };
    @Input() hamburgerMenuFlag: boolean;
    @Output() navExpandedEvent = new EventEmitter<boolean>();

    private readonly blankHeader = new HhsgSharedSideNavHeader();
    private readonly blankMenuItem = new HhsgSharedSideNavMenuItem();

    hasRouteGuardFailed = false;
    isHeaderExpanded = false;
    isNavExpanded = true;
    navigatedHeader: HhsgSharedSideNavHeader;
    navigatedMenuItem: HhsgSharedSideNavMenuItem;
    navigatedSubMenuItem: HhsgSharedSideNavMenuItem;
    selectedHeader: HhsgSharedSideNavHeader;
    selectedMenuItem: HhsgSharedSideNavMenuItem;
    selectedSubMenuItem: HhsgSharedSideNavMenuItem;

    constructor(private _router: Router,
        private _configService: HhsgSharedConfigService, private _userService: HhsgSharedUserService,) {
        
        this.applicationAbbreviation = this._configService.appSettings.applicationAbbreviation;
        this._userService.getUserProfile()
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((user: HhsgSharedUserProfile) => {
            this.user = user;
        });

        this.blankHeader.iconClass = "";
        this.blankHeader.label = "";
        this.blankHeader.menuItems = [];
        this.blankHeader.name = "";
        this.blankHeader.selectedIconClass = "";

        this.blankMenuItem.label = "";
        this.blankMenuItem.name = "";
        this.blankMenuItem.route = "";

        this.navigatedHeader = this.blankHeader;
        this.navigatedMenuItem = this.blankMenuItem;
        this.navigatedSubMenuItem = this.blankMenuItem;
        this.selectedHeader = this.blankHeader;
        this.selectedMenuItem = this.blankMenuItem;
        this.selectedSubMenuItem = this.blankMenuItem;
    }

    ngOnInit(): void {
        this._router.events.subscribe(event => {
            if (event instanceof GuardsCheckEnd) {
                this.hasRouteGuardFailed = !event.shouldActivate;

                this.updateSelectedItems();
            }

            if (event instanceof NavigationEnd) {
                this.navigatedHeader = this.blankHeader;
                this.navigatedMenuItem = this.blankMenuItem;
                this.navigatedSubMenuItem = this.blankMenuItem;

                this.updateSelectedItems();
            }
        });
    }

    private areHeadersEqual(headerOne: HhsgSharedSideNavHeader, headerTwo: HhsgSharedSideNavHeader): boolean {
        return headerOne.iconClass == headerTwo.iconClass &&
            headerOne.label == headerTwo.label &&
            headerOne.name == headerTwo.name &&
            headerOne.selectedIconClass == headerTwo.selectedIconClass;
    }

    private areMenuItemsEqual(menuItemOne: HhsgSharedSideNavMenuItem, menuItemTwo: HhsgSharedSideNavMenuItem): boolean {
        return menuItemOne.label == menuItemTwo.label &&
            menuItemOne.name == menuItemTwo.name &&
            menuItemOne.route == menuItemTwo.route;
    }

    public headerHasMenuItems(header: HhsgSharedSideNavHeader): boolean {
        return header && header.menuItems && header.menuItems.length > 0;
    }

    public headerSelected(header: HhsgSharedSideNavHeader) {
        if (this.headerHasMenuItems(header)) {
            if (!this.areHeadersEqual(header, this.selectedHeader)) {
                this.isHeaderExpanded = true;
            }
            else {
                this.isHeaderExpanded = !this.isHeaderExpanded;
            }
        }

        this.setHeaderSelected(header);
    }

    private headerRouted(header: HhsgSharedSideNavHeader) {
        this.navigatedHeader = header;
        this.navigatedMenuItem = this.blankMenuItem;
        this.navigatedSubMenuItem = this.blankMenuItem;
    }

    public isHeaderSelected(header: HhsgSharedSideNavHeader): boolean {
        return this.areHeadersEqual(header, this.selectedHeader);
    }

    public isMenuItemSelected(menuItem: HhsgSharedSideNavMenuItem): boolean {
        return this.areMenuItemsEqual(menuItem, this.selectedMenuItem);
    }

    private menuItemRouted(menuItem: HhsgSharedSideNavMenuItem) {
        this.navigatedMenuItem = menuItem;
        this.navigatedSubMenuItem = this.blankMenuItem;
    }

    public menuItemSelected(menuItem: HhsgSharedSideNavMenuItem) {
        this.selectedSubMenuItem = this.blankMenuItem;
        this.selectedMenuItem = menuItem;
    }

    public isSubMenuItemSelected(menuItem: HhsgSharedSideNavMenuItem): boolean {
        return this.areMenuItemsEqual(menuItem, this.selectedSubMenuItem);
    }

    private setHeaderSelected(header: HhsgSharedSideNavHeader) {
        if (!this.hasRouteGuardFailed || !header.routeUrl || header.routeUrl === '') {
            this.selectedHeader = header;
            this.selectedMenuItem = this.blankMenuItem;
            this.selectedSubMenuItem = this.blankMenuItem;
        }
    }

    private subMenuItemRouted(subMenuItem: HhsgSharedSideNavMenuItem) {
        this.navigatedSubMenuItem = subMenuItem;
    }

    public subMenuItemSelected(menuItem: HhsgSharedSideNavMenuItem) {
        this.selectedSubMenuItem = menuItem;

        //bug #97918 fix, if user tries clicking directly on the child sub menu, logic to filter the parent menu item
        if (this.selectedMenuItem == this.blankMenuItem) {

            this._headerLinks.filter(menuItem => menuItem && menuItem.menuItems && menuItem.menuItems.length > 0)
                .forEach(menuItem => {
                    menuItem.menuItems
                        .filter(subMenuItem => subMenuItem && subMenuItem.subMenuItems && subMenuItem.subMenuItems.length > 0)
                        .forEach(subMenuItem => {
                            var childMenuItem = subMenuItem.subMenuItems.find(item => item.route == this.selectedSubMenuItem.route);
                            if (childMenuItem == this.selectedSubMenuItem) {
                                this.selectedMenuItem = subMenuItem;
                                return;
                            }
                        });
                    if (this.selectedMenuItem != this.blankMenuItem) {
                        return;
                    }
                });
        }
    }

    /**
     * Method to pass the isNavExpanded flag to parent component, 
     * so that the parent component can adjust the width of the container
     */
    public toggleNavExpanded(): void {
        this.isNavExpanded = !this.isNavExpanded;
        this.isHeaderExpanded = this.isNavExpanded;
    }

    public showNavExpanded(): void {
        if (!this.isNavExpanded) {
            this.isNavExpanded = true;
        }
    }

    private updateSelectedItems() {
        if (this._headerLinks && this._headerLinks.length > 0) {
            let route = this._router.url;
            let selectedHeader = RouteUtil.getHeaderByRoute(this._headerLinks, route);
            let selectedMenu = RouteUtil.getMenuItemByRoute(this._headerLinks, route);
            let selectedSubMenuItem = RouteUtil.getSubMenuItemByRoute(this._headerLinks, route);

            if (selectedHeader) {
                this.setHeaderSelected(selectedHeader);
                this.selectedHeader = selectedHeader;
                if (!this.hasRouteGuardFailed) {
                    this.headerRouted(selectedHeader);
                }

                if ((!selectedHeader.menuItems || selectedHeader.menuItems && selectedHeader.menuItems.length == 0) && this.selectedMenuItem != this.blankMenuItem) {
                    this.selectedMenuItem = this.blankMenuItem;
                    this.selectedSubMenuItem = this.blankMenuItem;
                }
            }

            if (selectedHeader && selectedHeader.menuItems && selectedHeader.menuItems.length > 0 && selectedMenu) {
                this.menuItemSelected(selectedMenu);
                this.isHeaderExpanded = true;
                if (!this.hasRouteGuardFailed) {
                    this.menuItemRouted(selectedMenu);
                }
            }

            if (selectedSubMenuItem) {
                this.subMenuItemSelected(selectedSubMenuItem);

                if (!this.hasRouteGuardFailed) {
                    this.subMenuItemRouted(selectedSubMenuItem);
                }
            }
        } else {
            this.isNavExpanded = !(this.isAppHap() && !this.isHuronEmployee());
        }
    }

    ngOnDestroy(): void {
        this.visibilitySubscription.unsubscribe();
        this._unsubscribe$.next();
		this._unsubscribe$.complete();
    }

    public isAppHap(): boolean {
        return this.applicationAbbreviation == Constants.HuronAcceleratorsAbbreviation;
    }

    public isHuronEmployee(): boolean {
        if (!this.user) {
            return false;
        }

        return RoleUtil.isHuronEmployee(this.user.roles);
    }
}
